import React from 'react';

import { ADD_APP_PAGE, APP_SETUP_PAGE, AppPresets } from 'src/constants';
import { RouteContext } from 'src/context';
import {
  BreadCrumbBase,
  BreadCrumbItem,
} from 'src/legacy/components/UI/BreadCrumbBase';

import { useAppSetupPaths } from '../useAppSetupPaths';
import { useContentfulApps } from '../useContentfulApps';

import { useEditingApp } from './useEditingApp';

const SetupAppBreadCrumbs = () => {
  const { query } = React.useContext(RouteContext);
  const { getAppInfo } = useContentfulApps();
  const { moduleId, preset = '' } = query as {
    moduleId?: string;
    preset?: string;
  };
  const { addAppPagePath } = useAppSetupPaths();
  const [breadCrumbsItems, setBreadCrumbsItems] = React.useState<
    Array<BreadCrumbItem>
  >([]);
  const editingModule = useEditingApp();

  /**
   * Method to get the text value of last breadcrumb
   * @returns string
   */
  const getBreadcrumbTitle = () => {
    if (preset === AppPresets.PartnerApp) return 'a Partner App';

    if (preset === AppPresets.CustomApp) return 'a Custom App';

    return getAppInfo(preset)?.name || '';
  };

  /**
   * This hook is used to get the breadcrumbs items for the current page.
   * When moduleId === new then it will return the breadcrumbs for the new module page.
   * When moduleId !== new then it will return the breadcrumbs for the edit module page.
   */
  React.useEffect(() => {
    // initial breadcrumbs items
    let items = [
      {
        label: APP_SETUP_PAGE.label,
        path: APP_SETUP_PAGE.path,
      },
    ];

    // getting bread crumbs items when it is create module flow
    if (moduleId === 'new') {
      items = items.concat([
        {
          label: ADD_APP_PAGE.label,
          path: addAppPagePath,
        },
        {
          label: `Add ${getBreadcrumbTitle()}`,
          path: '',
        },
      ]);
      setBreadCrumbsItems(items);
      return;
    }

    // getting bread crumbs items when it is edit module flow
    if (editingModule) {
      // if the module is disabled, it should show Enable instead of Edit.
      // This is the scenario when we're enabling a core module.
      if (editingModule.disabled) {
        items = items.concat([
          {
            label: ADD_APP_PAGE.label,
            path: addAppPagePath,
          },
          {
            label: `Enable ${editingModule.label}`,
            path: '',
          },
        ]);
      } else {
        items.push({
          label: `Edit ${editingModule.label}`,
          path: '',
        });
      }

      setBreadCrumbsItems(items);
    }
  }, [editingModule]);

  return <BreadCrumbBase items={breadCrumbsItems} showOnlyTitleOnMobile />;
};

export default SetupAppBreadCrumbs;
